import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Loader2, Mail, Lock, UserPlus, User, Gift } from 'lucide-react';
import { authService } from '../services/authService';
import * as Sentry from '@sentry/react';

export default function Register() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const referralCode = searchParams.get('ref') || undefined;
    const targetBrandId = searchParams.get('brandId') || undefined;
    const isReferred = !!(referralCode && targetBrandId);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Watch for infinite loading spinners
    useEffect(() => {
        if (!isLoading) return;
        const timer = setTimeout(() => {
            Sentry.captureMessage("UI bloqueada en estado de carga: Componente Register", "warning");
        }, 10000);
        return () => clearTimeout(timer);
    }, [isLoading]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrorMessage('');

        if (password.length < 6) {
            setErrorMessage('La contraseña debe tener al menos 6 caracteres.');
            return;
        }

        if (password !== confirmPassword) {
            setErrorMessage('Las contraseñas no coinciden.');
            return;
        }

        setIsLoading(true);

        try {
            await authService.register(name, email, password, referralCode, targetBrandId);
            navigate('/login', {
                replace: true,
                state: { message: 'Cuenta creada correctamente. Inicia sesión.' },
            });
        } catch (err: any) {
            setErrorMessage(err.message || 'Error al crear la cuenta.');
            setIsLoading(false);
        }
    };

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white shadow-xl">
            <div className="flex flex-1 flex-col justify-center px-6 py-12">
                {/* Header */}
                <div className="mb-10 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-100">
                        <UserPlus size={32} className="text-indigo-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Crear cuenta</h1>
                    <p className="mt-2 text-sm text-gray-500">
                        Regístrate para empezar a acumular puntos y recompensas
                    </p>
                </div>

                {/* Referral Banner */}
                <div className={`mb-6 flex items-start gap-4 rounded-3xl bg-indigo-50 p-5 shadow-sm border border-indigo-100 ${isReferred ? 'block' : 'hidden'}`}>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                        <Gift size={24} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-bold text-indigo-800 mb-1">
                            ¡Has sido invitado!
                        </h3>
                        <p className="text-sm text-indigo-600/90 leading-relaxed">
                            Regístrate para ganar puntos extra en tu primera compra.
                        </p>
                    </div>
                </div>

                {/* Error Message */}
                <div className={`mb-6 rounded-2xl bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-600 transition-all ${errorMessage ? 'block' : 'hidden'}`}>
                    {errorMessage || '\u00A0'}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} translate="no" className="flex flex-col gap-4">
                    {/* Name */}
                    <div>
                        <label
                            htmlFor="name"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Nombre completo
                        </label>
                        <div className="relative">
                            <User
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="name"
                                type="text"
                                required
                                autoComplete="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Tu nombre"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label
                            htmlFor="register-email"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Correo electrónico
                        </label>
                        <div className="relative">
                            <Mail
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="register-email"
                                type="email"
                                required
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@correo.com"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div>
                        <label
                            htmlFor="register-password"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Contraseña
                        </label>
                        <div className="relative">
                            <Lock
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="register-password"
                                type="password"
                                required
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Mínimo 6 caracteres"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label
                            htmlFor="register-confirm"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Confirmar contraseña
                        </label>
                        <div className="relative">
                            <Lock
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="register-confirm"
                                type="password"
                                required
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repite la contraseña"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                <span>Creando cuenta...</span>
                            </>
                        ) : (
                            <span>Crear cuenta</span>
                        )}
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-gray-500">
                    ¿Ya tienes cuenta?{' '}
                    <Link
                        to="/login"
                        className="font-medium text-indigo-600 hover:text-indigo-700"
                    >
                        Inicia sesión
                    </Link>
                </p>

                <p className="mt-6 text-center text-xs text-gray-500">
                    Al registrarte, aceptas nuestros{' '}
                    <Link to="/terms" className="font-medium hover:text-indigo-600 transition-colors">Términos y Condiciones</Link>
                    {' '}y nuestra{' '}
                    <Link to="/privacy" className="font-medium hover:text-indigo-600 transition-colors">Política de Privacidad</Link>.
                </p>
            </div>
        </div>
    );
}
